import { addMinutes } from 'date-fns/addMinutes';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { buildUrl, unwrap } from './utils';
import { getStorageIdConnectCookie } from '@idConnect/identification/data/getStorageIdConnectCookie';
import { openInIframe } from '@shared/iFrameHandler/openInIframe';
import { denyConsent } from './actions';
import { callEventListeners } from '@shared/events/callEventListeners';

export const idConnectStart: Maker<Ctx, Snap, null> = ({
  makeEffect,
  makeEffectGroup,
}) => [
  makeEffect(
    'debounce',
    (_, { ctx, pass, skip }) => {
      const url = buildUrl(ctx, ['idConnectConfig', 'customUtiqHost'])
        ?.tryPathFrom(
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            .IDCONNECT_API_HOST,
        )
        ?.addPathSegment('data')
        .withTransactionId()
        .render();

      if (ctx.times.__retriggerIdConnectFlow !== undefined)
        if (ctx.axios.active.find((x) => x.req.url === url))
          return pass('active');

      return skip();
    },
    (_, { ctx }) => {
      delete ctx.times.__retriggerIdConnectFlow;
    },
  ),

  makeEffect(
    'get',
    (_, { ctx, snap, skip, pass }) => {
      if (snap.cookies.utiq_consent_status !== 'true' && !ctx.isIdConnect)
        return skip(`snap.cookies.utiq_consent_status !== 'true'`);

      if (
        snap.localStorage.utiqEligibility?.isEligible !== true &&
        !ctx.isIdConnect
      )
        return skip(`snap.localStorage.utiqEligibility?.isEligible !== true`);

      if (ctx.axios.active.find((x) => x.req.url?.includes('mno-precheck')))
        return skip('mno-precheck active');

      if (ctx.axios.active.find((x) => x.req.url?.includes('consent')))
        return skip('consent active');

      if (
        snap.localStorage.utiqDataCallTimestamp &&
        snap.now <
          addMinutes(new Date(snap.localStorage.utiqDataCallTimestamp), 10)
      )
        return skip('utiqDataCallTimestamp in date');

      const Utiq = unwrap(ctx.window.Utiq);
      if (!Utiq.data?.clientConfig)
        return skip('ctx.window.Utiq.data?.clientConfig undefined');

      if (getStorageIdConnectCookie(Utiq.data.clientConfig.IDCONNECT_CONFIG))
        return skip('getStorageIdConnectCookie() defined');

      const url = buildUrl(ctx, ['idConnectConfig', 'customUtiqHost'])
        ?.tryPathFrom(
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            .IDCONNECT_API_HOST,
        )
        ?.addPathSegment('data')
        .withTransactionId()
        .render();

      if (!url) return skip('url unbuildable');

      if (ctx.times.__retriggerIdConnectFlow === undefined) {
        if (ctx.axios.log.find((x) => x.req.url === url))
          return skip('logged and no retrigger required');
      } else {
        if (
          !Utiq.syncVersionCookies &&
          ctx.axios.log.find(
            (x) =>
              x.req.url === url &&
              x.whenReq > ctx.times.__retriggerIdConnectFlow!,
          )
        )
          return skip('logged and after __retriggerIdConnectFlow');
      }

      if (ctx.axios.active.find((x) => x.req.url === url))
        return skip('active');

      return pass(url);
    },

    (url, { ctx }) => {
      delete unwrap(ctx.window.Utiq).syncVersionCookies;
      delete ctx.times.__retriggerIdConnectFlow;
      ctx.axios.instance({
        method: 'get',
        url,
        withCredentials: true,
        timeout: 4000,
      });
    },
  ),

  makeEffectGroup(
    'handleIframeSrcDoc',
    (_, { ctx, skip, pass }) => {
      if (!ctx.axios.active[0]) return skip();

      const { res, req } = ctx.axios.active[0];
      if (!res) return skip();

      const url = buildUrl(ctx, ['idConnectConfig', 'customUtiqHost'])
        ?.tryPathFrom(
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            .IDCONNECT_API_HOST,
        )
        ?.addPathSegment('data')
        .withTransactionId()
        .render();

      if (req.url === url) return pass(res);
      else return skip('');
    },

    ({ makeEffect }) => [
      makeEffect(
        '200',
        (res, { pass, skip }) =>
          res.status === 200 && typeof res.data === 'string'
            ? pass(res.data)
            : skip(''),
        (iFrameUrl, { ctx }) => {
          ctx.axios.active.shift();
          openInIframe({
            iFrameUrl,
            iFrameId: 'GETDATA',
            isSrcDocUsed: true,
            environment: 'Utiq',
          });
        },
      ),

      makeEffect(
        '204',
        (res, { skip, pass, ctx }) => {
          if (res.status === 204) return pass(!ctx.times.utiqPassEverPopulated);

          return skip('');
        },

        (send_ids_not_set, { ctx, snap }) => {
          ctx.times.onFlowCompleted = snap.now;
          ctx.axios.active.shift();
          callEventListeners('onFlowCompleted', 'Utiq', { status: 'reload' });

          //TODO: no conditionals allowed in effect actions
          if (send_ids_not_set) {
            ctx.analytics.ids_not_set = snap.now;
          }

          denyConsent(snap, ctx);
        },
      ),
    ],
  ),
];
